import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import AuthCallback from './pages/AuthCallback';
import NeverOverpayLanding from './pages/neveroverpay/NeverOverpayLanding';
import NeverOverpayApp from './pages/neveroverpay/NeverOverpayApp';
import NeverOverpayCase from './pages/neveroverpay/NeverOverpayCase';
import NeverOverpayOps from './pages/neveroverpay/NeverOverpayOps';
import NeverOverpayAuth from './pages/neveroverpay/NeverOverpayAuth';
import NeverOverpayAdmin from './pages/neveroverpay/NeverOverpayAdmin';
import NeverOverpayGmailCallback from './pages/neveroverpay/NeverOverpayGmailCallback';
import NeverOverpayPlaidOAuth from './pages/neveroverpay/NeverOverpayPlaidOAuth';
import NeverOverpayOnboarding from './pages/neveroverpay/NeverOverpayOnboarding';
import NeverOverpaySettings from './pages/neveroverpay/NeverOverpaySettings';
import BrandMark from './pages/neveroverpay/BrandMark';
import { neveroverpayApi } from './pages/neveroverpay/neveroverpayApi';
import './pages/neveroverpay/neveroverpay.css';

const API_URL = (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL !== '')
  ? process.env.REACT_APP_BACKEND_URL
  : '';
const AUTH_CHECK_TIMEOUT_MS = 10000;
const AUTH_DENIED_STATUSES = new Set([401, 403]);

async function fetchCurrentUser(token) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), AUTH_CHECK_TIMEOUT_MS);
  try {
    const res = await fetch(`${API_URL}/api/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    });
    if (AUTH_DENIED_STATUSES.has(res.status)) {
      const error = new Error('auth denied');
      error.authDenied = true;
      throw error;
    }
    if (!res.ok) throw new Error('auth unavailable');
    return await res.json();
  } finally {
    clearTimeout(timeout);
  }
}

function tokenFromStorage() {
  let token = localStorage.getItem('token');
  if (!token) {
    const backup = localStorage.getItem('emerge_token_backup');
    if (backup) {
      localStorage.setItem('token', backup);
      localStorage.removeItem('emerge_token_backup');
      token = backup;
    }
  }
  return token;
}

const PrivateRoute = ({ children }) => {
  const location = useLocation();
  if (!tokenFromStorage()) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/auth?next=${next}`} replace />;
  }
  return children;
};

function OnboardingGate({ children }) {
  const location = useLocation();
  const [status, setStatus] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    neveroverpayApi.onboarding()
      .then((row) => {
        if (!cancelled) setStatus(row);
      })
      .catch(() => {
        if (!cancelled) setStatus({ complete: true });
      });
    return () => { cancelled = true; };
  }, [location.pathname]);

  if (!status) {
    return (
      <div className="np-wrap np-muted" style={{ padding: 40 }}>Starting your first savings path…</div>
    );
  }
  if (!status.complete) {
    return <Navigate to="/onboarding" replace />;
  }
  return children;
}

function NeverOverpayLegal({ kind }) {
  const title = kind === 'privacy' ? 'Privacy' : 'Terms';
  return (
    <div className="np-root">
      <div className="np-wrap" style={{ paddingTop: 48, paddingBottom: 72, maxWidth: 720 }}>
        <BrandMark href="/" kicker={title} />
        <h1 style={{ fontSize: 32, letterSpacing: '-0.04em', margin: '24px 0 12px' }}>{title}</h1>
        {kind === 'privacy' ? (
          <>
            <p className="np-muted" style={{ fontSize: 16 }}>
              BilledTwice stores the emails, bills, and bank activity you choose to share so we can recover money you are already owed. We do not sell that data.
            </p>
            <p className="np-muted" style={{ fontSize: 16, marginTop: 16 }}>You can allow, separately:</p>
            <ul className="np-list">
              <li>Reading recovery-related Gmail</li>
              <li>Sending one approved recovery email from Gmail</li>
              <li>Matching refunds to bank activity (Plaid or CSV)</li>
              <li>Reading files you paste or upload</li>
            </ul>
            <p className="np-muted" style={{ fontSize: 16, marginTop: 16 }}>
              We do not send, cancel, change, or accept anything until you approve the exact draft. You can turn a permission off or delete your data in Settings.
            </p>
          </>
        ) : (
          <>
            <p className="np-muted" style={{ fontSize: 16 }}>
              BilledTwice drafts recovery requests. Approving a request is not legal advice. You stay responsible for what is sent in your name.
            </p>
            <p className="np-muted" style={{ fontSize: 16, marginTop: 16 }}>
              Connecting Gmail or a bank is optional. Skip and paste a company email if you prefer. You can disconnect and delete your household data at any time.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function NeverOverpayNotFound() {
  return (
    <div className="np-root">
      <div className="np-wrap" style={{ paddingTop: 80 }}>
        <BrandMark />
        <h1 style={{ fontSize: 32, marginTop: 24 }}>Page not found</h1>
        <p className="np-muted" style={{ marginTop: 8 }}>This page is only for BilledTwice.</p>
        <p style={{ marginTop: 20 }}><a className="np-btn" href="/">Go home</a></p>
      </div>
    </div>
  );
}

function App() {
  const [user, setUser] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    const token = tokenFromStorage();
    if (!token) return undefined;
    fetchCurrentUser(token)
      .then((data) => {
        if (!cancelled) setUser(data);
      })
      .catch((error) => {
        if (!error.authDenied) return;
        localStorage.removeItem('token');
        localStorage.removeItem('emerge_token_backup');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="np-root" data-testid="neveroverpay-shell">
      <BrowserRouter>
        <ErrorBoundary>
          <Routes>
            <Route path="/" element={<NeverOverpayLanding />} />
            <Route path="/recover" element={<Navigate to="/" replace />} />
            <Route path="/neveroverpay" element={<Navigate to="/" replace />} />
            <Route path="/auth" element={<NeverOverpayAuth setUser={setUser} />} />
            <Route path="/login" element={<Navigate to="/auth" replace />} />
            <Route path="/recover/auth" element={<Navigate to="/auth" replace />} />
            <Route path="/auth/google/callback" element={<AuthCallback setUser={setUser} />} />
            <Route path="/onboarding" element={<PrivateRoute><NeverOverpayOnboarding /></PrivateRoute>} />
            <Route path="/app" element={<PrivateRoute><OnboardingGate><NeverOverpayApp user={user} /></OnboardingGate></PrivateRoute>} />
            <Route path="/recover/app" element={<Navigate to="/app" replace />} />
            <Route path="/cases/:caseId" element={<PrivateRoute><NeverOverpayCase user={user} /></PrivateRoute>} />
            <Route path="/recover/cases/:caseId" element={<Navigate to="/app" replace />} />
            <Route path="/ops" element={<PrivateRoute><OnboardingGate><NeverOverpayOps user={user} /></OnboardingGate></PrivateRoute>} />
            <Route path="/recover/ops" element={<Navigate to="/ops" replace />} />
            <Route path="/admin" element={<PrivateRoute><NeverOverpayAdmin user={user} /></PrivateRoute>} />
            <Route path="/gmail/callback" element={<PrivateRoute><NeverOverpayGmailCallback /></PrivateRoute>} />
            <Route path="/plaid/oauth" element={<PrivateRoute><NeverOverpayPlaidOAuth /></PrivateRoute>} />
            <Route path="/ledger" element={<PrivateRoute><OnboardingGate><NeverOverpayApp user={user} tab="ledger" /></OnboardingGate></PrivateRoute>} />
            <Route path="/recover/ledger" element={<Navigate to="/ledger" replace />} />
            <Route path="/settings" element={<PrivateRoute><OnboardingGate><NeverOverpaySettings /></OnboardingGate></PrivateRoute>} />
            <Route path="/privacy" element={<NeverOverpayLegal kind="privacy" />} />
            <Route path="/terms" element={<NeverOverpayLegal kind="terms" />} />
            <Route path="*" element={<NeverOverpayNotFound />} />
          </Routes>
        </ErrorBoundary>
      </BrowserRouter>
    </div>
  );
}

export default App;
